import {
  EnumSidebarMenu,
  EnumSidebarSubMenu,
} from 'modules/common/types/sidebar'
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from 'react'
import {
  SidebarContextReducerAction,
  useSidebarContextState,
} from './SidebarContext'

interface SidebarActions {
  selectMenu: (menuId: EnumSidebarMenu) => void
  selectSubMenu: (subMenuId: EnumSidebarSubMenu | undefined) => void
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
  selectFirstSubMenu: (menuId: EnumSidebarMenu) => void
}

const SidebarActionsContext = createContext<SidebarActions | undefined>(
  undefined
)

interface Props {
  dispatch: React.Dispatch<SidebarContextReducerAction>
}

const SidebarActionsContextProvider = ({
  dispatch,
  children,
}: PropsWithChildren<Props>) => {
  const { menus, selectedMenuId, selectedSubMenuId, isSidebarOpen } =
    useSidebarContextState()

  const selectMenu = useCallback(
    (menuId: EnumSidebarMenu) => {
      if (selectedMenuId !== menuId) {
        dispatch({
          type: 'set',
          payload: { selectedMenuId: menuId },
        })
      }
    },
    [dispatch, selectedMenuId]
  )

  const selectSubMenu = useCallback(
    (subMenuId: EnumSidebarSubMenu | undefined) => {
      if (selectedSubMenuId !== subMenuId) {
        dispatch({
          type: 'set',
          payload: {
            selectedSubMenuId: subMenuId,
          },
        })
      }
    },
    [dispatch, selectedSubMenuId]
  )

  const openSidebar = useCallback(
    () =>
      dispatch({
        type: 'set',
        payload: {
          isSidebarOpen: true,
        },
      }),
    [dispatch]
  )

  const closeSidebar = useCallback(
    () =>
      dispatch({
        type: 'set',
        payload: {
          isSidebarOpen: false,
        },
      }),
    [dispatch]
  )

  const toggleSidebar = useCallback(
    () =>
      dispatch({
        type: 'set',
        payload: {
          isSidebarOpen: !isSidebarOpen,
        },
      }),
    [dispatch, isSidebarOpen]
  )

  const selectFirstSubMenu = useCallback(
    (menuId: EnumSidebarMenu) => {
      const firstSubMenu = menus.find(({ id }) => id === menuId)?.subMenus?.[0]

      selectSubMenu(firstSubMenu?.id)
    },
    [menus, selectSubMenu]
  )

  return (
    <SidebarActionsContext.Provider
      value={{
        selectMenu,
        selectSubMenu,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        selectFirstSubMenu,
      }}
    >
      {children}
    </SidebarActionsContext.Provider>
  )
}

const useSidebarActionsContext = () => {
  const context = useContext(SidebarActionsContext)
  if (context === undefined) {
    throw new Error(
      'useSidebarActionsContext must be used within a SidebarActionsContextProvider'
    )
  }
  return context
}

export { SidebarActionsContextProvider, useSidebarActionsContext }
